import Router from 'next/router';
import { memo, useMemo } from 'react';

import { SidebarNavigationItem } from '@air/component-sidebar-navigation';
import { InfoIcon, PencilIcon, PlusIcon, Settings2Icon } from '@air/design-icons';
import { DropdownMenu, type DropdownMenuOption, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { useAirModal } from '@air/provider-modal';

import { CreateSavedFilterModal } from '~/components/SavedFilter/CreateSavedFilterModal';
import { EditSavedFilterModal } from '~/components/SavedFilter/EditSavedFilterModal';
import { useGetSavedFilters } from '~/components/SavedFilter/shared/hooks/useGetSavedFilters';
import { createSavedSearchRoute } from '~/constants/routes';
import { SIDEBAR_NAVIGATION_ITEM_SAVED_FILTERS, SIDEBAR_NAVIGATION_SAVED_FILTERS_EDIT } from '~/constants/testIDs';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canCreateSavedFilters, canEditSavedFilters } from '~/utils/permissions/workspacePermissions';

export const SavedFiltersNavigationItem = memo(() => {
  const { data: savedFiltersResponse } = useGetSavedFilters();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canAdd = canCreateSavedFilters(permissions);
  const canEdit = canEditSavedFilters(permissions);
  const [showCreateSavedFilterModal] = useAirModal(CreateSavedFilterModal);
  const [showEditSavedFilterModal] = useAirModal(EditSavedFilterModal);
  const numberOfSavedFilters = savedFiltersResponse?.data?.length ?? 0;

  const dropdownMenuOptions = useMemo<DropdownMenuOption[]>(() => {
    const options: DropdownMenuOption[] = [];

    if (savedFiltersResponse?.data?.length) {
      savedFiltersResponse?.data?.map((savedFilter) =>
        options.push({
          id: savedFilter.name,
          label: savedFilter.name,
          onSelect: () => Router.push(createSavedSearchRoute(savedFilter.id)),
          prefix: savedFilter.icon,
          suffix: canEdit && (
            <IconButton
              adsIcon
              appearance="ghost"
              color="gray"
              data-id={savedFilter.id}
              data-name={savedFilter.name}
              data-testid={SIDEBAR_NAVIGATION_SAVED_FILTERS_EDIT}
              icon={PencilIcon}
              label="Edit"
              // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
              onClick={(event) => {
                event.preventDefault();
                showEditSavedFilterModal({ savedFilter });
              }}
              size="small"
            />
          ),
          type: 'item',
        }),
      );
    }

    if (canAdd) {
      options.push({
        id: 'create',
        label: 'Create saved search',
        onSelect: () =>
          showCreateSavedFilterModal({
            filters: {},
            trackLocation: 'side-nav-saved-filters',
          }),
        prefix: <PlusIcon size="sm" />,
        suffix: (
          <Tooltip
            className="max-w-[240px]"
            label="Saved searches appear here for all workspace members and auto-update as new content fits their criteria."
          >
            <InfoIcon size="sm" />
          </Tooltip>
        ),
        type: 'item',
      });
    }

    return options;
  }, [canAdd, canEdit, savedFiltersResponse?.data, showCreateSavedFilterModal, showEditSavedFilterModal]);

  if (!(canAdd || !!savedFiltersResponse?.data.length)) {
    return null;
  }

  return (
    <DropdownMenu
      alignOffset={32}
      align="start"
      className="max-h-[320px] w-[240px] overflow-auto"
      trigger={
        <SidebarNavigationItem data-testid={SIDEBAR_NAVIGATION_ITEM_SAVED_FILTERS} prefix={<Settings2Icon size="sm" />}>
          <div>Saved searches {!!numberOfSavedFilters && ` (${numberOfSavedFilters})`}</div>
        </SidebarNavigationItem>
      }
    >
      {renderDropdownItems({ options: dropdownMenuOptions })}
    </DropdownMenu>
  );
});

SavedFiltersNavigationItem.displayName = 'SavedFiltersNavigationItem';
